import { useState } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';

import { confirmArrivalSchedule } from '#/api/srm/purchaseOrder/arrivalSchedule/confirm';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import {
  getPurchaseOrderApiErrorMessage,
  isPurchaseOrderVersionConflict,
} from '#/modules/srmMgmt/purchaseOrder/helper/apiError';
import { canConfirmArrivalSchedule } from '#/modules/srmMgmt/purchaseOrder/helper/arrivalScheduleStatus';
import { toast } from '#/utils/toast';
import {
  purchaseOrderArrivalScheduleDetailQueryKey,
  purchaseOrderArrivalScheduleListQueryKeyPrefix,
} from '#/utils/queryKeys';

const PurchaseOrderArrivalScheduleConfirmCell = ({
  purchaseOrderId,
  schedule,
  onMutated,
}) => {
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const [isConfirming, setIsConfirming] = useState(false);

  if (!canConfirmArrivalSchedule(schedule?.status)) {
    return null;
  }

  async function invalidateScheduleQueries() {
    if (!purchaseOrderId) {
      return;
    }
    await queryClient.invalidateQueries({
      queryKey: purchaseOrderArrivalScheduleListQueryKeyPrefix(purchaseOrderId),
    });
    if (schedule?.id) {
      await queryClient.invalidateQueries({
        queryKey: purchaseOrderArrivalScheduleDetailQueryKey(
          purchaseOrderId,
          schedule.id,
        ),
      });
    }
    await onMutated?.();
  }

  async function handleConfirm() {
    if (!purchaseOrderId || !schedule?.id || isConfirming) {
      return;
    }
    const ok = await confirm({
      title: '確認到貨排程',
      message: '確認後排程將變為「已確認」，確定確認？',
      confirmText: '確認',
    });
    if (!ok) {
      return;
    }
    setIsConfirming(true);
    try {
      const updatedAt = schedule?.record?.updatedAt ?? schedule?.updatedAt;
      const result = await confirmArrivalSchedule(
        purchaseOrderId,
        schedule.id,
        updatedAt ? { updatedAt } : {},
      );
      toast.success(result?.message || '已確認排程');
      await invalidateScheduleQueries();
    } catch (error) {
      toast.error(getPurchaseOrderApiErrorMessage(error, '確認排程失敗'));
      if (isPurchaseOrderVersionConflict(error)) {
        await invalidateScheduleQueries();
      }
    } finally {
      setIsConfirming(false);
    }
  }

  return (
    <>
      {ConfirmDialogHost}
      <Button
        type="button"
        size="sm"
        className="h-7"
        disabled={isConfirming}
        onClick={handleConfirm}
      >
        {isConfirming ? '確認中…' : '確認'}
      </Button>
    </>
  );
};

PurchaseOrderArrivalScheduleConfirmCell.propTypes = {
  purchaseOrderId: PropTypes.string,
  schedule: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    updatedAt: PropTypes.string,
    record: PropTypes.shape({
      updatedAt: PropTypes.string,
    }),
  }),
  onMutated: PropTypes.func,
};

export default PurchaseOrderArrivalScheduleConfirmCell;
